import { ApiError } from '@/lib/api'
import { fehlersatz } from '@/lib/fehlersatz'
import { pruefsatz } from '@/lib/pruefsatz'
import { uebersetze } from '@/lib/uebersetzen'
import type { ValidationError } from '@/types/api'

/**
 * Warum eine Änderung am Aufbau einer Baukasten-Seite abgelehnt wurde, als
 * Satz in der Sprache des Lesers.
 *
 * Der Server schickt einen Code (`error`) und bei einem ungültigen Ergebnis
 * die einzelnen Verstöße (`fehler`) oder abgelehnten Texte (`errors`). Aus dem
 * Panel heraus passiert das selten, die Knöpfe bieten nur Erlaubtes an. Es
 * zählt, wenn zwei Personen gleichzeitig umbauen oder ein Baustein im Repo
 * geändert wurde, während die Seite offen war.
 */
export function abschnittsfehler(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as {
      error?: unknown
      fehler?: { code?: unknown; wert?: unknown }[]
      errors?: ValidationError[]
    } | null
    const erster = body?.fehler?.[0]
    switch (body?.error) {
      case 'forbidden_no_struktur':
        return uebersetze('abschnitte.fehler.kein_recht')
      case 'no_plan':
        return uebersetze('abschnitte.fehler.kein_plan')
      case 'no_catalog':
        return uebersetze('abschnitte.fehler.kein_katalog')
      case 'unknown_section':
      case 'unknown_entry':
        return uebersetze('abschnitte.fehler.weg')
      case 'unknown_type':
        return uebersetze('abschnitte.fehler.baustein_unbekannt')
      case 'ohne_titel':
        return uebersetze('abschnitte.fehler.ohne_titel')
      case 'unbekannte_vorlage':
        return uebersetze('abschnitte.fehler.vorlage_unbekannt')
      case 'keine_adresse':
        return uebersetze('abschnitte.fehler.keine_adresse')
      case 'nur_entwurf':
        return uebersetze('abschnitte.fehler.nur_entwurf')
      case 'eigene_seite':
        return uebersetze('abschnitte.fehler.eigene_seite')
      case 'keine':
        return uebersetze('abschnitte.fehler.keine')
      case 'fremd':
        return uebersetze('abschnitte.fehler.fremd')
      case 'konflikt':
        return uebersetze('abschnitte.fehler.konflikt')
      case 'gleiche_adresse':
        return uebersetze('abschnitte.fehler.gleiche_adresse')
      case 'adresse_vergeben':
        return uebersetze('abschnitte.fehler.adresse_vergeben')
      case 'kein_menue':
        return uebersetze('abschnitte.fehler.kein_menue')
      case 'menue_ungueltig':
        if (erster?.code === 'unbekannte_seite') return uebersetze('abschnitte.fehler.menue_seite_weg')
        if (erster?.code === 'link_leer') return uebersetze('abschnitte.fehler.menue_link_leer')
        if (erster?.code === 'doppelt') return uebersetze('abschnitte.fehler.menue_doppelt')
        if (erster?.code === 'zu_viele') return uebersetze('abschnitte.fehler.menue_zu_viele', { max: String(erster.wert ?? '') })
        return uebersetze('abschnitte.fehler.ungueltig')
      case 'invalid_plan':
        if (erster?.code === 'too_many_sections') return uebersetze('abschnitte.fehler.zu_viele', { max: String(erster.wert ?? '') })
        if (erster?.code === 'too_few_entries') return uebersetze('abschnitte.fehler.zu_wenige_eintraege', { min: String(erster.wert ?? '') })
        if (erster?.code === 'too_many_entries') return uebersetze('abschnitte.fehler.zu_viele_eintraege', { max: String(erster.wert ?? '') })
        if (erster?.code === 'bad_anchor') return uebersetze('abschnitte.fehler.anker_ungueltig')
        if (erster?.code === 'duplicate_anchor') return uebersetze('abschnitte.fehler.anker_doppelt')
        if (erster?.code === 'unknown_spacing') return uebersetze('abschnitte.fehler.abstand_unbekannt')
        if (erster?.code === 'unknown_overlay') return uebersetze('abschnitte.fehler.schleier_unbekannt')
        if (erster?.code === 'unknown_device') return uebersetze('abschnitte.fehler.geraet_unbekannt')
        if (erster?.code === 'unknown_motion') return uebersetze('abschnitte.fehler.bewegung_unbekannt')
        if (erster?.code === 'cannot_hide') return uebersetze('abschnitte.fehler.ausblenden_nicht')
        return uebersetze('abschnitte.fehler.ungueltig')
      case 'invalid_texts': {
        const text = body.errors?.[0]
        return text ? pruefsatz(text) : uebersetze('abschnitte.fehler.ungueltig')
      }
    }
  }
  return fehlersatz(e, uebersetze('abschnitte.was'))
}
